import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { scheduleApi, therapistApi } from '../../services/api';
import { Schedule, Assignment, TimeSlot, Therapist } from '../../types';
import AssignmentModal from './AssignmentModal';
import { exportService } from '../../services/exportService';
import './ScheduleGrid.css';

interface ScheduleGridProps {
  scheduleId?: number;
  startDate?: string;
  endDate?: string;
}

const ScheduleGrid: React.FC<ScheduleGridProps> = ({ scheduleId, startDate, endDate }) => {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);

    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

  const loadScheduleData = useCallback(async () => {
    if (!scheduleId) return;
    
    console.log('Loading schedule data for scheduleId:', scheduleId);
    setLoading(true);
    setError(null);

    try {
      // Load schedule details and therapists in parallel
      // Note: assignments come with the schedule, no need for separate call
      const [scheduleResponse, therapistResponse] = await Promise.all([
        scheduleApi.getById(scheduleId),
        therapistApi.getAll({ active: true, limit: 100 })
      ]);

      console.log('Schedule response:', scheduleResponse);
      console.log('Therapist response:', therapistResponse);

      const scheduleData = scheduleResponse.data.schedule;
      const assignmentsData = scheduleData.assignments || []; // Get assignments from schedule
      const therapistsData = therapistResponse.data.therapists;

      console.log('Parsed schedule data:', scheduleData);
      console.log('Parsed assignments data:', assignmentsData);
      console.log('Parsed therapists data:', therapistsData);

      setSchedule(scheduleData);
      const normalizedAssignments = assignmentsData.map((assignment: any) => {
        let guestStudentData = assignment.guest_student;

        if (!guestStudentData && assignment.guest_student_name) {
          guestStudentData = {
            id: assignment.guest_student_id,
            full_name: assignment.guest_student_name,
            contact_info: assignment.guest_student_contact,
            notes: assignment.guest_student_notes
          };
        }

        if (typeof guestStudentData === 'string') {
          try {
            guestStudentData = JSON.parse(guestStudentData);
          } catch {
            guestStudentData = null;
          }
        }

        return {
          ...assignment,
          guestStudent: guestStudentData
            ? {
                id: guestStudentData.id,
                fullName: guestStudentData.full_name || guestStudentData.fullName || '',
                contactInfo: guestStudentData.contact_info || guestStudentData.contactInfo || undefined,
                notes: guestStudentData.notes || undefined
              }
            : undefined
        } as Assignment;
      });

      setAssignments(normalizedAssignments);
      setTherapists(therapistsData);

      // Generate time slots from schedule - prioritize session config time slots
      console.log('Schedule data received:', scheduleData);
      console.log('Session config available:', !!scheduleData.sessionConfig);
      console.log('Session config time slots available:', !!scheduleData.sessionConfig?.timeSlots);
      console.log('Legacy time slots available:', scheduleData.timeSlots?.length || 0);
      
      // Prioritize session config time slots over legacy time slots
      if (scheduleData.sessionConfig && scheduleData.sessionConfig.timeSlots) {
        console.log('✅ Using session config time slots');
        
        // Use the first day's time slots as the template (assuming all days have same structure)
        const timeSlotsByDay = scheduleData.sessionConfig.timeSlots;
        const availableDays = Object.keys(timeSlotsByDay);
        
        if (availableDays.length > 0) {
          const firstDay = availableDays[0];
          const firstDaySlots = timeSlotsByDay[firstDay as keyof typeof timeSlotsByDay] as any[];
          
          if (firstDaySlots && firstDaySlots.length > 0) {
            const convertedTimeSlots = firstDaySlots.map((slot: any) => ({
              id: slot.id || 0,
              startTime: slot.startTime || slot.start_time,
              endTime: slot.endTime || slot.end_time,
              slotName: slot.slotName || slot.session_title || '',
              isBreakTime: slot.isBreakTime || slot.is_break || false,
              sessionType: slot.sessionType || 'individual',
              therapistId: slot.therapistId || null,
              studentId: slot.studentId || null,
              assignmentId: slot.assignmentId || null,
              notes: slot.notes || ''
            }));
            
            setTimeSlots(convertedTimeSlots);
            console.log('✅ Converted time slots from session config:', convertedTimeSlots);
          } else {
            console.warn('⚠️ No time slots found in first day of session config');
            setTimeSlots([]);
          }
        } else {
          console.warn('⚠️ No days found in session config time slots');
          setTimeSlots([]);
        }
      } else if (scheduleData.timeSlots && scheduleData.timeSlots.length > 0) {
        console.log('⚠️ Using legacy time slots from schedule');
        setTimeSlots(scheduleData.timeSlots);
      } else {
        console.log('⚠️ No time slots available in schedule');
        setTimeSlots([]);
      }
      
      console.log('✅ Schedule data loaded successfully');
    } catch (err) {
      console.error('❌ Error loading schedule data:', err);
      setError('Failed to load schedule data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    console.log('ScheduleGrid useEffect triggered with scheduleId:', scheduleId);
    if (scheduleId) {
      loadScheduleData();
    } else {
      // Clear data when no schedule is selected
      setSchedule(null);
      setAssignments([]);
      setTimeSlots([]);
      setLoading(false);
    }
  }, [scheduleId, loadScheduleData]);


  const getAssignmentForSlot = (day: string, timeSlot: TimeSlot): Assignment | undefined => {
    // Normalize time format for comparison
    const normalizeTime = (time: string) => {
      if (!time) return '';
      // Remove seconds if present and ensure HH:MM format
      const parts = time.split(':');
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    };

    const slotStart = normalizeTime(timeSlot.startTime);
    const slotEnd = normalizeTime(timeSlot.endTime);

    // Debug logging
    console.log('Looking for assignment:', { day, slotStart, slotEnd });
    console.log('Available assignments:', assignments.map(a => ({
      id: a.id,
      day: a.dayOfWeek || (a as any).day_of_week,
      start: normalizeTime(a.startTime || (a as any).start_time),
      end: normalizeTime(a.endTime || (a as any).end_time),
      student: a.student?.first_name || (a.student as any)?.firstName
    })));

    const found = assignments.find(assignment => 
      (assignment.dayOfWeek || (assignment as any).day_of_week) === day &&
      normalizeTime(assignment.startTime || (assignment as any).start_time) === slotStart &&
      normalizeTime(assignment.endTime || (assignment as any).end_time) === slotEnd
    );

    console.log('Found assignment:', found);
    return found;
  };

  const handleCellClick = (day: string, timeSlot: TimeSlot, assignment?: Assignment) => {
    setSelectedDay(day);
    setSelectedTimeSlot(timeSlot);
    setSelectedAssignment(assignment || null);
    setShowModal(true);
  };

  const handleModalSave = () => {
    setShowModal(false);
    setSelectedTimeSlot(null);
    setSelectedDay('');
    setSelectedAssignment(null);
    // Reload the schedule data to reflect changes
    loadScheduleData();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTimeSlot(null);
    setSelectedDay('');
    setSelectedAssignment(null);
  };

  const handleExportPDF = async () => {
    if (!schedule) {
      alert('No schedule selected for export');
      return;
    }
    
    if (assignments.length === 0) {
      const confirmExport = window.confirm('This schedule appears to be empty. Do you still want to export it?');
      if (!confirmExport) return;
    }
    
    setExporting(true);
    try {
      // Make sure we have the latest data before exporting
      const scheduleWithTherapistName = {
        ...schedule,
        therapistName: schedule.therapistName || therapists.find(t => t.id === schedule.therapistId)?.name || 'Unknown Therapist'
      };
      
      console.log('Exporting schedule:', scheduleWithTherapistName);
      console.log('With assignments:', assignments);
      console.log('And therapists:', therapists);
      
      await exportService.quickExportPDF('schedule-grid', scheduleWithTherapistName, therapists);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };



  // Check for conflicts
  const checkConflicts = () => {
    const conflicts: { therapist: Assignment[]; student: Assignment[]; guest: Assignment[] } = {
      therapist: [],
      student: [],
      guest: []
    };

    assignments.forEach((assignment1, index) => {
      assignments.slice(index + 1).forEach(assignment2 => {
        // Check therapist conflicts
        if ((assignment1.therapistId || (assignment1 as any).therapist_id) && 
            (assignment1.therapistId || (assignment1 as any).therapist_id) === (assignment2.therapistId || (assignment2 as any).therapist_id) &&
            (assignment1.dayOfWeek || (assignment1 as any).day_of_week) === (assignment2.dayOfWeek || (assignment2 as any).day_of_week) &&
            (assignment1.startTime || (assignment1 as any).start_time) === (assignment2.startTime || (assignment2 as any).start_time)) {
          conflicts.therapist.push(assignment1, assignment2);
        }

        // Check student conflicts
        if ((assignment1.studentId || (assignment1 as any).student_id) && 
            (assignment1.studentId || (assignment1 as any).student_id) === (assignment2.studentId || (assignment2 as any).student_id) &&
            (assignment1.dayOfWeek || (assignment1 as any).day_of_week) === (assignment2.dayOfWeek || (assignment2 as any).day_of_week) &&
            (assignment1.startTime || (assignment1 as any).start_time) === (assignment2.startTime || (assignment2 as any).start_time)) {
          conflicts.student.push(assignment1, assignment2);
        }

        // Check guest student conflicts
        const guestId1 = assignment1.guestStudentId || (assignment1 as any).guest_student_id;
        const guestId2 = assignment2.guestStudentId || (assignment2 as any).guest_student_id;
        if (guestId1 && guestId2 &&
            guestId1 === guestId2 &&
            (assignment1.dayOfWeek || (assignment1 as any).day_of_week) === (assignment2.dayOfWeek || (assignment2 as any).day_of_week) &&
            (assignment1.startTime || (assignment1 as any).start_time) === (assignment2.startTime || (assignment2 as any).start_time)) {
          conflicts.guest.push(assignment1, assignment2);
        }
      });
    });

    return conflicts;
  };

  if (loading) {
    return (
      <div className="schedule-loading">
        <div className="loading-spinner"></div>
        <p>Loading schedule...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schedule-error">
        <p>Error: {error}</p>
        <button onClick={loadScheduleData} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  if (!schedule) {
    return (
      <div className="schedule-empty">
        <p>No schedule selected</p>
      </div>
    );
  }

  const conflicts = checkConflicts();
  const hasConflicts = conflicts.therapist.length > 0 || conflicts.student.length > 0 || conflicts.guest.length > 0;

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="schedule-grid-container">
        <div className="schedule-header">
          <div className="schedule-title-section">
            <h3>
              Schedule: {new Date(schedule.weekStartDate).toLocaleDateString()} - {new Date(schedule.weekEndDate).toLocaleDateString()}
            </h3>
            <div className="schedule-info">
              <span>Working Hours: {schedule.workingHoursStart} - {schedule.workingHoursEnd}</span>
              <span>Session Duration: {schedule.sessionDuration} minutes</span>
              {hasConflicts && (
                <span className="conflict-warning">
                  ⚠️ {conflicts.therapist.length + conflicts.student.length + conflicts.guest.length} conflicts detected
                </span>
              )}
            </div>
          </div>
          <div className="schedule-legend-section">
            <div className="schedule-actions">
              <button
                className="export-button"
                onClick={handleExportPDF}
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : '📄 Export PDF'}
              </button>
              {hasConflicts && (
                <button
                  className="conflict-button"
                  onClick={() => {
                    const conflictDetails = [
                      ...conflicts.therapist.map(a => `Therapist conflict: ${a.therapist?.name || 'Therapist'} at ${a.dayOfWeek} ${a.startTime}`),
                      ...conflicts.student.map(a => `Student conflict: ${(a.student?.first_name || '').trim()} ${(a.student?.last_name || '').trim()} at ${a.dayOfWeek} ${a.startTime}`),
                      ...conflicts.guest.map(a => `Guest student conflict: ${a.guestStudent?.fullName || 'Guest'} at ${a.dayOfWeek} ${a.startTime}`)
                    ].join('\n');
                    alert(`Conflicts found:\n\n${conflictDetails}`);
                  }}
                >
                  ⚠️ View Conflicts
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="schedule-grid" id="schedule-grid">
          <table className="schedule-table">
            <thead>
              <tr>
                <th className="weekday-header">Weekday</th>
                {timeSlots.map(timeSlot => (
                  <th key={timeSlot.id} className="session-header">
                    <div className="session-title">{timeSlot.slotName}</div>
                    <div className="time-range">{timeSlot.startTime} - {timeSlot.endTime}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daysOfWeek.map(day => (
                <tr key={day}>
                  <td className="weekday-cell">
                    <strong>{day}</strong>
                  </td>
                  {timeSlots.map(timeSlot => {
                    const assignment = getAssignmentForSlot(day, timeSlot);
                    const hasConflict = assignment && (
                      conflicts.therapist.some(a => a.id === assignment.id) ||
                      conflicts.student.some(a => a.id === assignment.id) ||
                      conflicts.guest.some(a => a.id === assignment.id)
                    );
                    
                    return (
                      <td 
                        key={`${day}-${timeSlot.id}`} 
                        className={`assignment-cell ${hasConflict ? 'conflict' : ''}`}
                        onClick={() => handleCellClick(day, timeSlot, assignment)}
                        style={{ cursor: 'pointer' }}
                      >
                        {assignment ? (
                          <div className={`assigned-student ${(assignment.assignmentType || (assignment as any).assignment_type) === 'break' ? 'break-assignment' : ''}`}>
                            <div className="student-name">
                              {(assignment.assignmentType || (assignment as any).assignment_type) === 'break'
                                ? 'Break'
                                : assignment.student
                                  ? ((assignment.student as any).name ||
                                     (assignment.student.first_name && assignment.student.last_name
                                       ? `${assignment.student.first_name} ${assignment.student.last_name}`
                                       : 'Unknown Student'))
                                  : assignment.guestStudent
                                    ? assignment.guestStudent.fullName || 'Guest Student'
                                    : 'Guest Student'}
                            </div>
                            {assignment.notes && (
                              <div className="assignment-notes">{assignment.notes}</div>
                            )}
                          </div>
                        ) : (
                          <div className="empty-slot">
                            <span>Click to add assignment</span>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Assignment Modal */}
        {showModal && selectedTimeSlot && scheduleId && (
          <AssignmentModal
            isOpen={showModal}
            assignment={selectedAssignment}
            timeSlot={selectedTimeSlot}
            day={selectedDay}
            scheduleId={scheduleId}
            schedule={schedule}
            onSave={handleModalSave}
            onClose={handleModalClose}
          />
        )}
      </div>
    </DndProvider>
  );
};

export default ScheduleGrid;
